// Updated FeatureCategoryInfo.jsx - Fixed color picker alignment
import React, { useEffect, useState } from "react";
import {
  ThinTextField,
  ThinDropdown,
  AddButton,
} from "../../common/GeneralComponent";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { fetchData } from "../../../utils/functions";
import { useDispatch } from "react-redux";
import { SketchPicker } from "react-color";
import { colornames } from "color-name-list";

const FeatureCategoryInfo = ({
  categoryId,
  categoryData,
  onCategoryCreated,
  onCategoryUpdated,
  onNext,
  isEditMode,
  showNextButton,
  showUpdateOnly,
  showSubmitAndNext,
  stepCompleted,
}) => {
  const [features, setFeatures] = useState([]);
  const [featureSubTypes, setFeatureSubTypes] = useState([]);
  const [color, setColor] = useState("#000000");
  const [colorPickerVisible, setColorPickerVisible] = useState(false);
  const [colorSearch, setColorSearch] = useState("");
  const [loader, setLoader] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      feature_id: "",
      feature_sub_type_id: "",
      category_name: "",
      category_code: "",
      color_code: "",
      status: 1,
    },
    validationSchema: Yup.object({
      feature_id: Yup.string().required("Feature is required"),
      feature_sub_type_id: Yup.string().required(
        "Feature Sub Type is required"
      ),
      category_name: Yup.string().required("Category name is required")
      .max(100, "Category name must be 100 characters or less"),
      category_code: Yup.string().required("Category code is required"),
      status: Yup.number().required().oneOf([0, 1], "Invalid status"),
      color_code: Yup.string().matches(
        /^#([0-9A-F]{3}){1,2}$/i,
        "Invalid hex color code"
      ),
    }),
    onSubmit: async (values) => {
      try {
        setLoader(true);

        if (isEditMode && categoryId) {
          // Update existing category
          await fetchData(
            `features-category-update/${categoryId}`,
            "PUT",
            values,
            {},
            {},
            navigate,
            "feature-category-view",
            dispatch
          );
          toast.success("Feature category updated successfully");
          if (onCategoryUpdated) onCategoryUpdated();
        } else {
          // Create new category
          const response = await fetchData(
            "features-category-create",
            "POST",
            values,
            {},
            {},
            navigate,
            "",
            dispatch
          );
          if (response?.data?.feature_category_id) {
            if (onCategoryCreated)
              onCategoryCreated(response.data.feature_category_id, values);
          }
        }
      } catch (error) {
        console.error(error.message || "Submission failed");
        toast.error("Submission failed");
      } finally {
        setLoader(false);
      }
    },
  });

  const colorMap = colornames.reduce((acc, item) => {
    acc[item.name.toLowerCase()] = item.hex;
    return acc;
  }, {});

  const handleSearchChange = (e) => {
    const input = e.target.value.toLowerCase();
    setColorSearch(input);
    if (colorMap[input]) {
      setColor(colorMap[input]);
      formik.setFieldValue("color_code", colorMap[input]);
    } else if (input === "") {
      setColor("#000000");
    }
  };

  const handleColorChange = (color) => {
    formik.setFieldValue("color_code", color.hex);
    setColor(color.hex);
  };

  const fetchFeatures = async () => {
    try {
      const res = await fetchData("features", "GET", null, {}, {}, navigate);
      setFeatures(res?.data || []);
    } catch {
      toast.error("Failed to load features");
    }
  };

  const fetchFeatureSubTypes = async (featureId) => {
    try {
      const res = await fetchData(
        `subtypes?feature_id=${featureId}`,
        "GET",
        null,
        {},
        {},
        navigate
      );
      setFeatureSubTypes(res?.data || []);
      if (!isEditMode) {
        formik.setFieldValue("feature_sub_type_id", "");
      }
    } catch {
      toast.error("Failed to load feature sub types");
    }
  };

  const handleSubmit = () => {
    formik.handleSubmit();
  };

  const handleNext = () => {
    if (stepCompleted && onNext) {
      onNext();
    } else {
      toast.error(
        "Please submit the form first before proceeding to the next step"
      );
    }
  };

  useEffect(() => {
    fetchFeatures();

    if (isEditMode && categoryData) {
      formik.setValues({
        feature_id: categoryData.feature_id || "",
        feature_sub_type_id: categoryData.feature_sub_type_id || "",
        category_name: categoryData.category_name || "",
        category_code: categoryData.category_code || "",
        color_code: categoryData.color_code || "",
        status: categoryData.status || 1,
      });
      setColor(categoryData.color_code || "#000000");
      if (categoryData.feature_id) {
        fetchFeatureSubTypes(categoryData.feature_id);
      }
    }
  }, [categoryData, isEditMode]);

  useEffect(() => {
    if (formik.values.feature_id) {
      fetchFeatureSubTypes(formik.values.feature_id);
    } else {
      setFeatureSubTypes([]);
      if (!isEditMode) {
        formik.setFieldValue("feature_sub_type_id", "");
      }
    }
  }, [formik.values.feature_id]);

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {/* Feature Dropdown */}
        <div>
          <ThinDropdown
            label="Select Feature"
            value={formik.values.feature_id}
            onChange={(e) => formik.setFieldValue("feature_id", e.target.value)}
            options={[
              ...features.map((f) => ({
                value: f.feature_id,
                label: f.feature_name,
              })),
            ]}
          />
          {formik.touched.feature_id && formik.errors.feature_id && (
            <p className="text-sm text-red-500 mt-1">
              {formik.errors.feature_id}
            </p>
          )}
        </div>

        {/* Feature Sub Type Dropdown */}
        <div>
          <ThinDropdown
            label="Select Feature Sub Type"
            value={formik.values.feature_sub_type_id}
            onChange={(e) =>
              formik.setFieldValue("feature_sub_type_id", e.target.value)
            }
            options={[
              ...featureSubTypes.map((s) => ({
                value: s.feature_sub_type_id,
                label: s.feature_sub_type,
              })),
            ]}
          />
          {formik.touched.feature_sub_type_id &&
            formik.errors.feature_sub_type_id && (
              <p className="text-sm text-red-500 mt-1">
                {formik.errors.feature_sub_type_id}
              </p>
            )}
        </div>

        {/* Category Name */}
        <div>
          <ThinTextField
            label="Category Name"
            name="category_name"
            value={formik.values.category_name}
            onChange={formik.handleChange}
          />
          {formik.touched.category_name && formik.errors.category_name && (
            <p className="text-sm text-red-500 mt-1">
              {formik.errors.category_name}
            </p>
          )}
        </div>

        {/* Category Code */}
        <div>
          <ThinTextField
            label="Category Code"
            name="category_code"
            value={formik.values.category_code}
            onChange={formik.handleChange}
          />
          {formik.touched.category_code && formik.errors.category_code && (
            <p className="text-sm text-red-500 mt-1">
              {formik.errors.category_code}
            </p>
          )}
        </div>

        {/* Color Picker */}
        <div>
          <label className="block text-[0.9rem] mt-1 text-[#565656] font-[InterLight]">
            Select Color
          </label>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              className="w-16 h-8 border border-[#dde2e9] rounded-[0.35rem]"
              onClick={() => setColorPickerVisible(!colorPickerVisible)}
              style={{ backgroundColor: formik.values.color_code || "#000000" }}
            >
              <span className="sr-only">Pick a color</span>
            </button>
            <div className="relative flex-1">
              <input
                type="text"
                className="text-[15px] w-full font-[InterLight] text-[#565656] bg-[#ebeef2] focus:bg-[#dde2e9] py-2 px-4 outline-none border-[#dde2e9] border rounded-[0.35rem] h-[42px]"
                name="color_code"
                value={formik.values.color_code} // ✅ always show hex from backend or picker
                onChange={(e) =>
                  formik.setFieldValue("color_code", e.target.value)
                }
                placeholder="#000000"
              />

              {colorPickerVisible && (
                <div className="absolute z-10 mt-1 left-0">
                  <SketchPicker
                    color={formik.values.color_code || "#000000"}
                    onChangeComplete={handleColorChange}
                    disableAlpha={true}
                  />
                </div>
              )}
            </div>
          </div>
          {formik.touched.color_code && formik.errors.color_code && (
            <p className="text-sm text-red-500 mt-1">
              {formik.errors.color_code}
            </p>
          )}
        </div>

        {/* Status Dropdown */}
        <div>
          <ThinDropdown
            label="Status"
            options={[
              { value: 1, label: "Active" },
              { value: 0, label: "Inactive" },
            ]}
            value={formik.values.status}
            onChange={(e) =>
              formik.setFieldValue("status", parseInt(e.target.value))
            }
          />
          {formik.touched.status && formik.errors.status && (
            <p className="text-sm text-red-500 mt-1">{formik.errors.status}</p>
          )}
        </div>
      </div>

      <div className="pt-4 flex gap-3 justify-end">
        {showUpdateOnly && (
          <AddButton text="Update" type="submit" disabled={loader} />
        )}
        {showSubmitAndNext && (
          <>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loader}
              className={`${
                loader ? "bg-gray-400" : "bg-teal-600 hover:bg-teal-700"
              } text-white px-4 py-2 rounded-lg transition duration-200 font-medium flex items-center gap-2`}
            >
              {loader ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Submitting..
                </>
              ) : (
                "Submit"
              )}
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={!stepCompleted || loader}
              className={`
                py-[0.375rem] px-[0.75rem] rounded-lg font-[InterLight] lg:text-[15px] text-[12px] 
                bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85 
                border-green flex items-center gap-2 
                ${
                  !stepCompleted || loader
                    ? "cursor-not-allowed opacity-80 bg-gray-400"
                    : ""
                }
              `}
            >
              Next
            </button>
          </>
        )}
        {showNextButton && !isEditMode && (
          <AddButton text="Next" type="button" onClick={handleNext} />
        )}
      </div>
    </form>
  );
};

export default FeatureCategoryInfo;
